import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import { Images, SVGs } from "../assets";
import { useAuthStore } from "../stores";
import Button from "./Button";
import LanguagePicker, { type LanguagePickerOption } from "./LanguagePicker";
import Modal, { type ModalRef } from "./Modal";
import Text from "./Text";
import * as React from "react";

export type HeaderProps = {
  brand?: string;
  onLogin?: () => void;
  className?: string;
};

export type AppUiLocale = "en" | "cz";

export const LANGUAGE_PICKER_OPTIONS: LanguagePickerOption<AppUiLocale>[] = [
  {
    code: "en",
    flag: SVGs.flagUK,
    label: "English",
  },
  {
    code: "cz",
    flag: SVGs.flagCZ,
    label: "Čeština",
  },
];

export default function Header({ className }: HeaderProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const logoutModalRef = React.useRef<ModalRef | null>(null);

  return (
    <header
      className={twMerge(
        "sticky top-0 z-[100] h-[80px] border-b border-background bg-surface text-text-primary opacity-100",
        className,
      )}
    >
      <div className="flex h-full w-full items-center justify-between gap-6  px-[20px] lg:px-[120px] py-[20px]">
        <img
          alt="Health 4 Travel"
          src={SVGs.logo}
          onClick={() => navigate("/")}
          className="h-[25.8px] w-auto cursor-pointer"
        />
        <div className="flex items-center">
          <Button
            color="secondaryDark"
            children={t("general.logout")}
            className="rounded-[2px] h-[22px] px-[7px]"
            onClick={() => logoutModalRef.current?.open()}
          />
          <Modal ref={logoutModalRef} title="Logout">
            <Modal.Confirmation
              description="Are you sure to logout?"
              buttons={[
                {
                  children: "No",
                  color: "secondary",
                  variant: "outline",
                  onClick: () => logoutModalRef.current?.close(),
                },
                {
                  children: "Yes",
                  color: "secondaryDark",
                  onClick: () => {
                    logoutModalRef.current?.close();
                    logout();
                  },
                },
              ]}
            />
          </Modal>
          <div className="ml-[16px] flex items-center gap-[8px]">
            <img
              src={Images.avatar}
              alt={user?.name}
              className="w-[24px] h-[24px] rounded-full"
            />
            <Text>{`Agent: ${user?.name}`}</Text>
          </div>
          <LanguagePicker
            className="ml-[24px]"
            options={LANGUAGE_PICKER_OPTIONS}
            value={i18n.resolvedLanguage ?? LANGUAGE_PICKER_OPTIONS[0].code}
            onChange={(code) => {
              void i18n.changeLanguage(code);
            }}
          />
        </div>
      </div>
    </header>
  );
}
